import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { EditActionBar } from '../shared/EditActionBar';
import type { Driver07Data, GestoriAdvisorRow } from '../../../api/apiTypesConductRisk';
import { useConductRiskDriver } from '../../../api/hooks/useConductRiskDriver';
import { formatHighlights } from '../../../utils/Format';

const fmtN    = (n: number) => n.toLocaleString('it-IT');
const fmtPctR = (n: number) => `${Math.round(n)}%`;

// La vista tabellare filtra le righe con total_gl_advisors === 0 (vedi sotto):
// una riga vuota "pura" (0 advisor) resterebbe invisibile e non editabile.
// Seminiamo quindi total_gl_advisors: 1 (minimo per superare il filtro > 0),
// cosi' la riga appena aggiunta compare ed e' modificabile dall'utente.
const emptyGestoriAdvisorRow = (): GestoriAdvisorRow => ({
  regional_division:       '',
  areas:                   0,
  branches:                0,
  total_gl_advisors:       1,
  awarded_gl_advisors:     0,
  non_awarded_gl_advisors: 0,
  awarded_pct:             0,
});

const numCls = 'w-16 text-right bg-white text-[#1B365D] border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:border-[#1B365D]/50 tabular-nums';
const txtCls = 'w-full min-w-[7rem] bg-white border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-xs text-[#1B365D] focus:outline-none focus:border-[#1B365D]/50';

/* ─── Main ───────────────────────────────────────────────── */
const GestoriGlobalAdvisorContainer = ({ reportId }: { reportId: string }) => {
  const { data: remoteData, loading, error, save, isReadOnly } = useConductRiskDriver<Driver07Data>(reportId, 7);

  const [data,      setData]      = useState<Driver07Data | null>(null);
  const [savedData, setSavedData] = useState<Driver07Data | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (remoteData) {
      setData(remoteData);
      setSavedData(remoteData);
    }
  }, [remoteData]);

  const handleAnnulla = () => { if (savedData) setData(savedData); setIsEditing(false); };
  const handleSalva   = async () => {
    if (!data) return;
    await save(data);
    setSavedData(data);
    setIsEditing(false);
  };

  // L'array renderizzato in tabella e' filtrato: gli indici della UI e
  // dell'array sorgente non coincidono. Poiche' anche il nome della Direzione
  // Regionale e' ora editabile (non puo' fare da chiave), gli updater lavorano
  // sull'indice REALE nell'array sorgente (srcIdx), che resta stabile.
  const updateRow = (srcIdx: number, field: keyof GestoriAdvisorRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      rows: {
        ...prev.rows,
        rows: prev.rows.rows.map((r, ri) => ri === srcIdx ? { ...r, [field]: val } : r),
      },
    } : prev));
  };
  const updateRowName = (srcIdx: number, value: string) => {
    setData(prev => (prev ? {
      ...prev,
      rows: {
        ...prev.rows,
        rows: prev.rows.rows.map((r, ri) => ri === srcIdx ? { ...r, regional_division: value } : r),
      },
    } : prev));
  };

  /* ─── Totali (numerico / nome) ─── */
  const updateTotals = (field: keyof GestoriAdvisorRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      rows: { ...prev.rows, totals: { ...prev.rows.totals, [field]: val } },
    } : prev));
  };
  const updateTotalsName = (value: string) => {
    setData(prev => (prev ? {
      ...prev,
      rows: { ...prev.rows, totals: { ...prev.rows.totals, regional_division: value } },
    } : prev));
  };

  const addRow = () => {
    setData(prev => (prev ? {
      ...prev,
      rows: {
        ...prev.rows,
        rows: [...prev.rows.rows, emptyGestoriAdvisorRow()],
      },
    } : prev));
  };

  const updateHighlights = (value: string) => {
    setData(prev => (prev ? { ...prev, highlights: value } : prev));
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-12 h-12 border-[5px] border-gray-200 border-t-[#1B365D] rounded-full animate-spin" />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="p-6 text-sm text-gray-400">Nessun dato disponibile per questo driver.</div>;
  }

  // Metodologia condivisa (Compliance UAT): nascondere le Direzioni Regionali
  // che non hanno alcun Global Advisor con CHK IN_FORZA = 1. Il BE include
  // ancora la riga con total_gl_advisors = 0 (es. DIREZIONE FILIALE DIGITALE);
  // filtriamo qui in UI. I totali del BE non cambiano perche' le righe
  // filtrate contribuiscono con 0.
  // Conservo l'indice sorgente (srcIdx) accanto alla riga, cosi' gli updater
  // possono agire sull'array non filtrato anche quando il nome cambia.
  const rows   = data.rows.rows
    .map((row, srcIdx) => ({ row, srcIdx }))
    .filter(x => x.row.total_gl_advisors > 0);
  const totals = data.rows.totals;

  return (
    <div className="p-6 space-y-5">

      {/* Edit action bar */}
      <div className="flex justify-end">
        <EditActionBar
          isEditing={isEditing}
          onProcedi={() => setIsEditing(true)}
          onAnnulla={handleAnnulla}
          onSalva={handleSalva}
          disabled={isReadOnly}
        />
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
          <div className="text-3xl font-bold text-[#1B365D]">{fmtN(totals.total_gl_advisors)}</div>
          <div className="text-[11px] text-gray-400 mt-1">TOT GL Advisor in forza</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
          <div className="text-3xl font-bold text-emerald-600">{fmtN(totals.awarded_gl_advisors)}</div>
          <div className="text-[11px] text-gray-400 mt-1">
            GL Advisor a premio ({fmtPctR(totals.awarded_pct)})
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
          <div className="text-3xl font-bold text-amber-600">{fmtN(totals.non_awarded_gl_advisors)}</div>
          <div className="text-[11px] text-gray-400 mt-1">
            GL Advisor no premio ({fmtPctR(100 - totals.awarded_pct)})
          </div>
        </div>
      </div>

      {/* Highlights */}
      <div className="px-4 py-3 rounded-xl bg-[#1B365D]/5 border border-[#1B365D]/10 text-xs text-[#1B365D]/80 leading-relaxed">
        <span className="font-semibold">Highlights: </span>
        {isEditing ? (
          <textarea
            className="w-full mt-1 p-2 text-xs text-[#1B365D]/80 leading-relaxed border border-[#1B365D]/20 rounded-lg resize-y focus:outline-none focus:border-[#1B365D]/40 bg-white/70 min-h-[100px]"
            value={data.highlights}
            onChange={e => updateHighlights(e.target.value)}
          />
        ) : (
          <span className="whitespace-pre-wrap">{formatHighlights(data.highlights)}</span>
        )}
      </div>

      {/* Tabella */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <p className="text-xs font-semibold text-[#1B365D]">
            Gestori Global Advisor per Direzione Regionale — {data.period}
          </p>
          <span className="text-[11px] text-[#1B365D] font-semibold bg-[#1B365D]/5 border border-[#1B365D]/10 px-2.5 py-0.5 rounded-full">
            {fmtN(totals.total_gl_advisors)} GL Advisor · {fmtN(totals.areas)} aree · {fmtN(totals.branches)} filiali
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 text-gray-400 uppercase text-[8px] tracking-wide">
                <th className="text-left  px-5 py-2.5 font-semibold">Direzione Regionale</th>
                <th className="text-right px-4 py-2.5 font-semibold">Aree</th>
                <th className="text-right px-4 py-2.5 font-semibold">Filiali</th>
                <th className="text-right px-4 py-2.5 font-semibold">TOT GL Advisor</th>
                <th className="text-right px-4 py-2.5 font-semibold text-emerald-600">GL Advisor a Premio</th>
                <th className="text-right px-4 py-2.5 font-semibold text-amber-500">GL Advisor no Premio</th>
                <th className="text-right px-4 py-2.5 font-semibold">% a premio</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-50">
              {rows.map(({ row, srcIdx }, i) => (
                <motion.tr
                  key={srcIdx}
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="hover:bg-gray-50/60 transition-colors"
                >
                  <td className="px-5 py-3 font-medium text-[#1B365D] whitespace-nowrap">
                    {isEditing
                      ? <input type="text" className={txtCls} value={row.regional_division} onChange={e => updateRowName(srcIdx, e.target.value)} />
                      : row.regional_division}
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-500">
                    {isEditing
                      ? <input type="number" className={numCls} value={row.areas} onChange={e => updateRow(srcIdx,'areas', e.target.value)} />
                      : fmtN(row.areas)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-500">
                    {isEditing
                      ? <input type="number" className={numCls} value={row.branches} onChange={e => updateRow(srcIdx,'branches', e.target.value)} />
                      : fmtN(row.branches)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-600 font-medium">
                    {isEditing
                      ? <input type="number" className={numCls} value={row.total_gl_advisors} onChange={e => updateRow(srcIdx,'total_gl_advisors', e.target.value)} />
                      : fmtN(row.total_gl_advisors)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold text-emerald-600">
                    {isEditing
                      ? <input type="number" className={numCls} value={row.awarded_gl_advisors} onChange={e => updateRow(srcIdx,'awarded_gl_advisors', e.target.value)} />
                      : fmtN(row.awarded_gl_advisors)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold text-amber-600">
                    {isEditing
                      ? <input type="number" className={numCls} value={row.non_awarded_gl_advisors} onChange={e => updateRow(srcIdx,'non_awarded_gl_advisors', e.target.value)} />
                      : fmtN(row.non_awarded_gl_advisors)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isEditing
                      ? <input type="number" className={numCls} value={row.awarded_pct} onChange={e => updateRow(srcIdx,'awarded_pct', e.target.value)} />
                      : <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                          row.awarded_pct >= 50
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : row.awarded_pct >= 30
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-red-50 text-red-600 border-red-200'
                        }`}>
                          {fmtPctR(row.awarded_pct)}
                        </span>}
                  </td>
                </motion.tr>
              ))}
            </tbody>

            <tfoot>
              <tr className="bg-[#1B365D] text-white text-[11px] font-semibold">
                <td className="px-5 py-3">
                  {isEditing ? <input type="text" className={txtCls} value={totals.regional_division} onChange={e => updateTotalsName(e.target.value)} /> : totals.regional_division}
                </td>
                <td className="px-4 py-3 text-right">
                  {isEditing ? <input type="number" className={numCls} value={totals.areas} onChange={e => updateTotals('areas', e.target.value)} /> : fmtN(totals.areas)}
                </td>
                <td className="px-4 py-3 text-right">
                  {isEditing ? <input type="number" className={numCls} value={totals.branches} onChange={e => updateTotals('branches', e.target.value)} /> : fmtN(totals.branches)}
                </td>
                <td className="px-4 py-3 text-right">
                  {isEditing ? <input type="number" className={numCls} value={totals.total_gl_advisors} onChange={e => updateTotals('total_gl_advisors', e.target.value)} /> : fmtN(totals.total_gl_advisors)}
                </td>
                <td className="px-4 py-3 text-right">
                  {isEditing ? <input type="number" className={numCls} value={totals.awarded_gl_advisors} onChange={e => updateTotals('awarded_gl_advisors', e.target.value)} /> : fmtN(totals.awarded_gl_advisors)}
                </td>
                <td className="px-4 py-3 text-right">
                  {isEditing ? <input type="number" className={numCls} value={totals.non_awarded_gl_advisors} onChange={e => updateTotals('non_awarded_gl_advisors', e.target.value)} /> : fmtN(totals.non_awarded_gl_advisors)}
                </td>
                <td className="px-4 py-3 text-right">
                  {isEditing ? <input type="number" className={numCls} value={totals.awarded_pct} onChange={e => updateTotals('awarded_pct', e.target.value)} /> : fmtPctR(totals.awarded_pct)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {isEditing && (
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={() => addRow()}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
          </button>
        </div>
      )}
    </div>
  );
};

export default GestoriGlobalAdvisorContainer;
